// medplum-booking.js — ONLINE-PRIMARY booking write path. The browser books a
// visit DIRECTLY as the logged-in caller (RLS-scoped by their own token via the
// shared getMedplum() session — see medplum-client.js), replacing the Express
// POST /scheduling/book and POST /appointments routes. This is the browser-direct
// successor the reception/queue/tasks screens adopt in Wave 2 (they still call
// the Express route today; wiring is a separate change).
//
// WHY ONE ATOMIC TRANSACTION BUNDLE (not the per-resource offline outbox the
// walk-in / quick-add paths use). A booking creates THREE interdependent
// resources — Patient, Appointment, Encounter. If the Encounter write failed
// after the Patient/Appointment landed we'd leak an orphan Patient + a phantom
// Appointment with no visit. A FHIR (Fast Healthcare Interoperability Resources)
// `transaction` Bundle commits all three or none — the correct property for a
// booking. WAVE-0 proved a booking-shaped transaction Bundle executes atomically
// under a NON-admin receptionist token, so this needs no service account.
// (Trade-off: a transaction Bundle is ONLINE-ONLY — it is not captured/replayed
// by the offline outbox, which models per-resource entries, not bundles. Booking
// is a receptionist-at-the-desk online action, so this is acceptable; the
// disk-first Express flow and the outbox walk-in path were offline-capable, so
// this is a deliberate parity difference — see the module report / OPD_REBUILD_LOG.)
//
// TWO WAVE-0 TRAPS this module MUST respect (both are load-bearing):
//   1. `ifNoneExist=identifier=sys|+91…` SILENTLY MISSES on a raw `+` (the query
//      parser reads it as a space) → a DUPLICATE PATIENT. Every identifier VALUE
//      in an ifNoneExist is run through encodeURIComponent (identifierParam()).
//   2. A transaction entry's urn:uuid fullUrl MUST be a syntactically VALID UUID
//      (randomUuid()); a prefixed key like `urn:uuid:enc-1234` silently ORPHANS
//      the children that reference it. So the fullUrl keys are real UUIDs and the
//      readable OPD slugs (pat-…/appt-…/enc-…) live in resource.identifier only.
//
// EXTENSION URLs are the https://healther.io/opd/* CANONICAL set the client
// shapers (shape/queue-row.js, shape/encounter-view.js, shape/opd-state.js) read
// back — NOT the server state-machine.mjs http://…/StructureDefinition/opd-state
// URL, which is main-1-only and would render the row as DRAFT/stateless to the
// online-primary shapers (see medplum-callin.js header + shape/opd-state.js).
import { getMedplum, authHeader as defaultAuthHeader } from './medplum-client.js';
import {
  SLUG_SYSTEMS, deterministicPatientId, randomUuid,
} from './outbox-ops.js';
import { wallClockToInstant } from './clinic-time.js';
import { apiUrl } from './net-base.js';

// ── Canonical OPD extension / identifier systems (parity with the server) ──────
const OPD_STATE_EXT = 'https://healther.io/opd/state';   // shape/opd-state.js OPD_STATE_EXTENSION_URL
const OPD_TOKEN_EXT = 'https://healther.io/opd/token';   // queue display token (an EXTENSION, not an identifier)
const PHONE_IDENTIFIER_SYSTEM = 'https://healther.io/opd/phone';        // human contact key
const DEPT_SLUG_SYSTEM = 'https://healther.io/opd/department-slug';     // Organization dept-slug identifier
// server ID_SYSTEMS.APPOINTMENT_SLUG (medplum-store.mjs) — outbox-ops SLUG_SYSTEMS
// has no APPOINTMENT arm, so it's named here to keep the mirror's slug identifier.
const APPOINTMENT_SLUG_SYSTEM = 'https://healther.io/opd/appointment-slug';
export const BOOKING_SLOT_SYSTEM = 'https://healther.io/opd/booking-slot';
export const SCHEDULING_HELP_SYSTEM = 'https://healther.io/opd/scheduling-help';
const TASK_TYPE_SYSTEM = 'https://healther.io/opd/task-type';
const HEALTHER_TASK_EXT = 'http://healther.io/fhir/StructureDefinition/opd-healther';

// Default slot length used to derive Appointment.end from start when the caller
// gives no explicit end — FHIR constraint app-2 (start requires end). Mirrors the
// server upsertAppointment default (state-machine.mjs) and the 15-min OPD slot.
const DEFAULT_SLOT_MIN = 15;

// Last-10 of an Indian mobile — the phone identifier value we stamp (so a phone
// lookup / the kiosk Bot's phone search finds the patient). '+91 98765 43210'
// and '9876543210' collapse to the same value.
const digitsOf = (v) => String(v || '').replace(/\D+/g, '').slice(-10);

/**
 * Build a `identifier=<system>|<encoded value>` query param for an ifNoneExist /
 * search — the ONE place the WAVE-0 encoding rule lives. The system is emitted
 * raw (it is a stable URL with no query-hostile characters) and the `|` separator
 * is literal; ONLY the value is percent-encoded, so a `+91…` phone or any value
 * with a `+`, space, or `&` can never be misparsed into a silent conditional-
 * create MISS (which creates a duplicate Patient). Matches the established idiom
 * in medplum-patient-create.js / medplum-encounter.js / the kiosk Bot's nextToken.
 */
export function identifierParam(system, value) {
  return `identifier=${system}|${encodeURIComponent(String(value))}`;
}

function schedulingHelpKey({ patientId, desiredDate, departmentId, doctorId, reason }) {
  const clean = (value, fallback) => String(value || fallback)
    .trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
  return [
    'scheduling-help',
    clean(patientId, 'patient'),
    clean(desiredDate, 'any-date'),
    clean(departmentId, 'any-department'),
    clean(doctorId, 'any-doctor'),
    clean(reason, 'help'),
  ].join('-');
}

/** Pure FHIR Task body for an explicit reception scheduling handoff. */
export function buildSchedulingHelpTask({
  patientId,
  patientName,
  desiredDate,
  departmentId,
  doctorId,
  doctorName,
  reason = 'scheduling-help',
  requester,
} = {}) {
  if (!patientId) throw new Error('scheduling help requires a patientId');
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(patientId))) {
    throw new Error('scheduling help requires a real Medplum Patient UUID');
  }
  const key = schedulingHelpKey({ patientId, desiredDate, departmentId, doctorId, reason });
  const dateText = desiredDate ? ` for ${desiredDate}` : '';
  const doctorText = doctorName ? ` with ${doctorName}` : '';
  const departmentText = departmentId ? ` in ${departmentId}` : '';
  const reasonText = String(reason || 'scheduling-help').replace(/-/g, ' ');
  const description = `Help ${patientName || 'patient'} arrange an OPD appointment${dateText}${doctorText}${departmentText} (${reasonText}).`;

  return {
    key,
    task: {
      resourceType: 'Task',
      identifier: [{ system: SCHEDULING_HELP_SYSTEM, value: key }],
      status: 'requested',
      intent: 'order',
      priority: 'routine',
      code: {
        text: 'Scheduling help',
        coding: [{ system: TASK_TYPE_SYSTEM, code: 'scheduling-help', display: 'Scheduling help' }],
      },
      description,
      authoredOn: new Date().toISOString(),
      for: { reference: `Patient/${patientId}`, ...(patientName ? { display: patientName } : {}) },
      ...(requester ? { requester } : {}),
      performerType: [{ text: 'reception' }],
      input: [
        ...(desiredDate ? [{ type: { text: 'desired-date' }, valueDate: desiredDate }] : []),
        ...(departmentId ? [{ type: { text: 'department' }, valueString: departmentId }] : []),
        ...(doctorId ? [{ type: { text: 'doctor' }, valueReference: { reference: `Practitioner/${doctorId}`, ...(doctorName ? { display: doctorName } : {}) } }] : []),
        { type: { text: 'reason' }, valueString: String(reason || 'scheduling-help') },
      ],
      extension: [{
        url: HEALTHER_TASK_EXT,
        valueString: JSON.stringify({
          kind: 'opd-task', taskType: 'scheduling-help', ownerId: null,
          performerRole: 'reception', dept: departmentId || null,
          createdBy: requester?.reference?.startsWith('Practitioner/')
            ? requester.reference.split('/')[1]
            : null,
        }),
      }],
    },
  };
}

/**
 * Create-or-return the reception scheduling Task as the logged-in caller.
 * Conditional create makes a lost acknowledgement/retry converge on one work
 * item; the Task stays unassigned (`requested`) and is explicitly for reception.
 */
export async function requestSchedulingHelp(args = {}) {
  const { medplum } = await getMedplum();
  const profile = typeof medplum.getProfileAsync === 'function'
    ? await medplum.getProfileAsync()
    : (typeof medplum.getProfile === 'function' ? medplum.getProfile() : null);
  const requester = profile?.id
    ? { reference: `${profile.resourceType}/${profile.id}`, display: practitionerDisplay(profile) || undefined }
    : null;
  const { key, task } = buildSchedulingHelpTask({ ...args, requester });
  const saved = await medplum.createResourceIfNoneExist(
    task,
    `${identifierParam(SCHEDULING_HELP_SYSTEM, key)}&status=requested,ready,in-progress,on-hold`,
  );
  const taskId = saved?.id || null;
  if (!taskId) throw new Error('scheduling-help conditional create returned no Task id');
  try { medplum.invalidateSearches('Task'); } catch (_) { /* optional client cache */ }
  return { ok: true, taskId };
}

function practitionerDisplay(profile) {
  const name = profile?.name?.[0];
  if (!name) return '';
  return name.text || [...(name.given || []), name.family].filter(Boolean).join(' ');
}

/**
 * The FHIR Patient body — kept BYTE-FOR-BYTE in lock-step with
 * medplum-patient-create.js buildPatientBody (that module's builder is not
 * exported, so it is replicated here; a create from either door must round-trip
 * through the reception edit dialog and every reader identically). `slug` is the
 * composite phone+name idempotency key stamped as an identifier so the
 * transaction's ifNoneExist matches it.
 */
export function buildPatientBody({ name, age, sex, phone, language, slug }) {
  const body = { resourceType: 'Patient' };

  const nm = String(name || '').trim();
  if (nm) body.name = [{ text: nm }];

  const phoneStr = String(phone || '').trim();
  const phoneKey = digitsOf(phoneStr);
  if (phoneStr) body.telecom = [{ system: 'phone', value: phoneStr }];
  const identifiers = [];
  if (phoneKey.length >= 10) identifiers.push({ system: PHONE_IDENTIFIER_SYSTEM, value: phoneKey });
  if (slug) identifiers.push({ system: SLUG_SYSTEMS.PATIENT, value: slug });
  if (identifiers.length) body.identifier = identifiers;

  if (sex === 'F') body.gender = 'female';
  else if (sex === 'M') body.gender = 'male';

  if (age !== undefined && age !== null && String(age).trim() !== '') {
    const n = Number(age);
    if (Number.isFinite(n) && n >= 0 && n <= 130) {
      body.birthDate = String(new Date().getUTCFullYear() - Math.round(n)) + '-01-01';
    }
  }

  const lang = String(language || '').trim();
  if (lang) {
    body.communication = [
      { language: { coding: [{ system: 'urn:ietf:bcp:47', code: lang }], text: lang }, preferred: true },
    ];
  }

  return body;
}

// Resolve the effective Appointment.start as a REAL UTC instant. Callers pass
// EITHER a pre-composed `startISO` instant (the slot pickers already compute this
// via clinic-time.js wallClockToInstant), OR a clinic wall-clock { date, time }
// this module composes through the SAME clinic-timezone discipline — so a booked
// instant is correct regardless of the device's timezone. 'walk-in' / empty ⇒ now.
function resolveStart({ startISO, date, time }) {
  const walkIn = !startISO || startISO === 'walk-in';
  if (walkIn) {
    if (date && time) {
      const iso = wallClockToInstant(String(date).slice(0, 10), time);
      if (iso) return { walkIn: false, start: iso };
    }
    return { walkIn: true, start: new Date().toISOString() };
  }
  const t = Date.parse(String(startISO));
  if (!Number.isFinite(t)) {
    throw Object.assign(new Error('startISO must be an ISO datetime or "walk-in"'), { code: 'bad-start', status: 400 });
  }
  return { walkIn: false, start: new Date(t).toISOString() };
}

/**
 * Build the atomic booking transaction Bundle — PURE (no I/O, no Medplum), so the
 * exact wire shape is unit-testable without a client. Returns { bundle, keys },
 * where `keys` carries the urn:uuid fullUrls + slugs the caller maps back to real
 * ids from the transaction-response.
 *
 * @param {object} p
 * @param {object} p.patient        { name, phone, age, sex, language }
 * @param {string} [p.patientSlug]  composite phone+name idempotency key (from
 *                                   deterministicPatientId, salted when forceNew);
 *                                   omitted when booking an EXISTING patientId.
 * @param {string} [p.patientId]    existing Medplum Patient UUID → referenced
 *                                   directly, NO Patient entry / conditional-create.
 * @param {string} p.start          Appointment.start (real UTC instant)
 * @param {string} [p.end]          explicit end; else start + DEFAULT_SLOT_MIN
 * @param {string} [p.doctorId]     Practitioner UUID (participant + appt actor)
 * @param {string} [p.doctorName]   display for the participant
 * @param {object} [p.department]   { reference?, display? } resolved dept Organization
 * @param {string} [p.departmentName] dept display for Appointment.serviceCategory
 * @param {string} [p.token]        walk-in queue token → OPD_TOKEN extension (only
 *                                  when a $mint-token seam supplied one; see notes)
 */
export function buildBookingBundle({
  patient = {}, patientSlug, patientId, start, end,
  doctorId, doctorName, department, departmentName, token, reason,
  appointmentSlug = null, encounterSlug = null, slotKey = null,
} = {}) {
  // urn:uuid fullUrls MUST be valid UUIDs (WAVE-0 trap 2). The readable OPD slugs
  // ride in resource.identifier, never as the urn:uuid key.
  const patientUuid = randomUuid();
  const apptUuid = randomUuid();
  const encUuid = randomUuid();
  const apptSlug = appointmentSlug || ('appt-' + randomUuid().slice(0, 12));
  const encSlug = encounterSlug || ('enc-' + randomUuid().slice(0, 12));

  // Reference the patient by its urn:uuid (new, created in this same bundle) or by
  // its real Patient/<uuid> (an existing patient chosen at the desk / kiosk).
  const patientRef = patientId ? `Patient/${patientId}` : `urn:uuid:${patientUuid}`;
  const displayName = String(patient.name || '').trim() || undefined;

  const endIso = end || (Number.isFinite(Date.parse(start))
    ? new Date(Date.parse(start) + DEFAULT_SLOT_MIN * 60 * 1000).toISOString()
    : undefined);

  const entry = [];

  // 1) Patient — conditional-create on the COMPOSITE phone+name slug so a genuine
  //    REBOOK of the same person reuses their chart (idempotent) while two family
  //    members on ONE phone stay distinct (the owner-decided anti-merge key, see
  //    medplum-patient-create.js). Skipped entirely for an existing patientId.
  if (!patientId) {
    entry.push({
      fullUrl: `urn:uuid:${patientUuid}`,
      resource: buildPatientBody({ ...patient, slug: patientSlug }),
      request: {
        method: 'POST',
        url: 'Patient',
        ...(patientSlug ? { ifNoneExist: identifierParam(SLUG_SYSTEMS.PATIENT, patientSlug) } : {}),
      },
    });
  }

  // 2) Appointment — FHIR-correct booking (status booked, patient + practitioner
  //    participants, true instants). Conditional-create on its own slug so a lost-
  //    ack retry of THIS bundle returns the same Appointment, never a twin.
  entry.push({
    fullUrl: `urn:uuid:${apptUuid}`,
    resource: {
      resourceType: 'Appointment',
      status: 'booked',
      identifier: [
        { system: APPOINTMENT_SLUG_SYSTEM, value: apptSlug },
        ...(slotKey ? [{ system: BOOKING_SLOT_SYSTEM, value: slotKey }] : []),
      ],
      start,
      ...(endIso ? { end: endIso } : {}),
      participant: [
        { actor: { reference: patientRef, ...(displayName ? { display: displayName } : {}) }, status: 'accepted' },
        ...(doctorId ? [{ actor: { reference: `Practitioner/${doctorId}`, ...(doctorName ? { display: doctorName } : {}) }, status: 'accepted' }] : []),
      ],
      ...(departmentName ? { serviceCategory: [{ text: departmentName }] } : {}),
      ...(String(reason || '').trim() ? { description: String(reason).trim() } : {}),
    },
    request: {
      method: 'POST', url: 'Appointment',
      ifNoneExist: slotKey
        ? `${identifierParam(BOOKING_SLOT_SYSTEM, slotKey)}&status:not=cancelled`
        : identifierParam(APPOINTMENT_SLUG_SYSTEM, apptSlug),
    },
  });

  // 3) Encounter — AMB (ambulatory), linked to the Appointment and (best-effort)
  //    the department Organization. Conditional-create on the encounter slug
  //    (retry-safety + the slug↔UUID identifier the /consult shim and resend-link
  //    resolve through).
  //
  //    STATE: a walk-in that DREW a live queue token is at the desk NOW, so it is
  //    stamped READY + status 'arrived' — the EXACT payoff the offline path's
  //    token-backfill Bot produces (mint token → READY → 'arrived'), so an online
  //    walk-in is indistinguishable from a drained one and the calling console
  //    (medplum-callin.js, which acts on READY/CALLED) picks it up instead of
  //    skipping a stateless 'Not started' DRAFT row. A booking with NO token — an
  //    advance/scheduled slot, or a follow-up whose token is assigned at check-in —
  //    stays DRAFT/planned (server parity). The token IS the "in the live queue"
  //    marker, exactly as the Bot couples minting with the READY advance. (opd/state
  //    values per shape/opd-state.js STATES; 'arrived' matches the Bot's checked-in
  //    walk-in Encounter.status — READY otherwise collapses to 'planned' in the
  //    generic FHIR_STATUS map, but a walk-in has physically arrived.)
  const encState = token ? 'READY' : 'DRAFT';
  const encStatus = token ? 'arrived' : 'planned';
  const encExtension = [{ url: OPD_STATE_EXT, valueString: encState }];
  if (token) encExtension.push({ url: OPD_TOKEN_EXT, valueString: token });
  entry.push({
    fullUrl: `urn:uuid:${encUuid}`,
    resource: {
      resourceType: 'Encounter',
      status: encStatus, // 'arrived' for a checked-in walk-in, else 'planned' (DRAFT)
      class: { system: 'http://terminology.hl7.org/CodeSystem/v3-ActCode', code: 'AMB', display: 'ambulatory' },
      identifier: [
        { system: SLUG_SYSTEMS.ENCOUNTER, value: encSlug },
        ...(slotKey ? [{ system: BOOKING_SLOT_SYSTEM, value: slotKey }] : []),
      ],
      subject: { reference: patientRef, ...(displayName ? { display: displayName } : {}) },
      ...(department && department.reference
        ? { serviceProvider: { reference: department.reference, ...(department.display ? { display: department.display } : {}) } }
        : {}),
      appointment: [{ reference: `urn:uuid:${apptUuid}` }],
      period: { start },
      ...(doctorId ? { participant: [{ individual: { reference: `Practitioner/${doctorId}`, ...(doctorName ? { display: doctorName } : {}) } }] } : {}),
      extension: encExtension,
    },
    request: {
      method: 'POST', url: 'Encounter',
      ifNoneExist: slotKey
        ? `${identifierParam(BOOKING_SLOT_SYSTEM, slotKey)}&status:not=cancelled`
        : identifierParam(SLUG_SYSTEMS.ENCOUNTER, encSlug),
    },
  });

  return {
    bundle: { resourceType: 'Bundle', type: 'transaction', entry },
    keys: { patientUuid, apptUuid, encUuid, apptSlug, encSlug, hasPatientEntry: !patientId },
  };
}

// Parse `ResourceType/<id>[/_history/x]` out of a transaction-response
// entry.response.location. Returns the bare id or null.
function idFromLocation(location) {
  const m = /([A-Za-z]+)\/([^/]+)/.exec(String(location || ''));
  return m ? m[2] : null;
}

// The department Organization reference for a dept slug, resolved as the caller
// (RLS applies). Real Organization/<uuid> when the dept has synced (so the
// row-level `Encounter?service-provider=%department` criterion can match), else a
// display-only hint. Best-effort: a read failure never blocks booking — the
// Encounter.participant=doctor already satisfies the write criterion (the
// serviceProvider ref is FHIR-nice, not RLS-required; see medplum-callin.js).
async function resolveDepartmentOrg(medplum, deptSlug, departmentName) {
  const slug = String(deptSlug || '').trim();
  if (!slug) return null;
  const display = departmentName || undefined;
  const org = await medplum.searchOne('Organization', identifierParam(DEPT_SLUG_SYSTEM, slug));
  if (org && org.id) return { reference: `Organization/${org.id}`, display: org.name || display };
  throw Object.assign(new Error(`department-not-found: ${slug}`), { code: 'department-not-found', status: 400 });
}

// Best-effort magic-link (WhatsApp intake link) send. Magic-link token minting is
// HMAC-signed with a SERVER-side secret (never shipped to the browser), so the
// link stays on the thin Express gateway — we POST the surviving resend-link
// route (appointment-ingestion.mjs mountResendLinkRoute) with the caller's
// bearer. Booking MUST succeed even if this fails (offline, rate-limited, or the
// route's doctor/admin role gate rejecting a receptionist) — returns { link,
// token } on success, { link: null, token: null } otherwise. NEVER throws.
async function sendMagicLink(encounterId, { fetchImpl, authHeader }) {
  try {
    const res = await fetchImpl(apiUrl(`/api/v1/opd/encounters/${encodeURIComponent(encounterId)}/resend-link`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: '{}',
    });
    if (!res || !res.ok) return { link: null, token: null };
    const j = await res.json().catch(() => ({}));
    return { link: (j && j.link) || null, token: (j && j.token) || null };
  } catch (_) {
    return { link: null, token: null };
  }
}

/**
 * Book a visit as the logged-in caller — the browser-direct successor of POST
 * /scheduling/book + POST /appointments. Executes ONE atomic Patient(+conditional-
 * create) / Appointment / Encounter transaction Bundle, then (best-effort) fires
 * the intake magic link.
 *
 * WALK-IN vs ADVANCE. `startISO` empty or 'walk-in' ⇒ walk-in (start = now); any
 * ISO instant ⇒ advance booking at that instant. A `{ date, time }` clinic wall-
 * clock is composed to a true instant via clinic-time.js (either form is accepted).
 *
 * DOCTOR ROUTING IS THE CALLER'S JOB. The server's schedule-aware auto-routing
 * (resolveDoctorForBooking — recurring-doctor / dept / general-medicine fallback)
 * reads the staff directory + availability, which the SCREEN already loads (the
 * reception dialog resolves the doctor from the availability API and passes
 * doctorId). This lib takes doctorId/doctorName/departmentId as INPUTS and does
 * NOT re-derive them — a deliberate read/write split (see the module report).
 *
 * QUEUE TOKEN. A sequential per-department daily token ('A-001') needs a SHARED
 * cross-user counter. The opd-receptionist AccessPolicy grants Basic only under
 * `Basic?author=%profile`, so a shared counter is invisible across desks —
 * client-side compare-and-swap is NOT viable and NO counter is built here. Pass a
 * `mintToken(deptSlug) => Promise<string>` seam (a small `$mint-token` Bot,
 * flagged for the coordinator to build) to stamp a walk-in's token atomically in
 * the bundle. Without it, `token` is null (advance bookings get their token at
 * check-in anyway — server parity).
 *
 * @returns {Promise<object>} DTO the screens consume (UUIDs primary, _medplumId
 *   populated for Tasks.jsx): { ok, encounterId, encounterSlug, appointmentId,
 *   patientId, patientExisted, token, walkIn, link, magicToken, patient,
 *   appointment, encounter }.
 * @throws SlotTakenError (advance, slot already booked), or a real transport /
 *   write error (fail loud — the redesign invariant).
 */
export async function bookAppointment({
  patient = {}, patientId, forceNewPatient,
  doctorId, doctorName, departmentId, departmentName,
  startISO, endISO, date, time, source, reason, idempotencyKey = null,
} = {}, {
  mintToken, checkSlot = true, sendLink = true,
  fetchImpl, authHeader,
} = {}) {
  const { medplum } = await getMedplum();
  const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : null);
  const doAuth = authHeader || defaultAuthHeader;

  const name = String(patient.name || '').trim();
  if (!name && !patientId) {
    throw Object.assign(new Error('patient.name required'), { code: 'bad-request', status: 400 });
  }

  const { walkIn, start } = resolveStart({ startISO, date, time });

  // Fast feedback for an ADVANCE booking to a specific doctor. This pre-search
  // is not the concurrency lock; the conditional-create transaction below is.
  // Never blocks a walk-in (walk-ins start now, no slot). A failed check is a
  // failed booking: proceeding would turn an authorization/transport error into
  // a possible double-booking.
  if (!walkIn && checkSlot && doctorId) {
    const clash = await medplum.searchOne(
      'Appointment',
      `actor=Practitioner/${encodeURIComponent(doctorId)}&date=${encodeURIComponent(start)}&status=booked`,
    );
    if (clash && clash.id) {
      throw Object.assign(new Error('slot-taken'), { code: 'slot-taken', status: 409, slot: start, doctorId });
    }
  }

  // Composite phone+name idempotency key (null when too thin to be safe). forceNew
  // salts it so an explicit "create new anyway" cannot collapse to the surfaced
  // duplicate — same contract as createPatientAsCaller. Skipped for an existing id.
  let patientSlug;
  if (!patientId) {
    patientSlug = await deterministicPatientId({ phone: patient.phone, name });
    if (forceNewPatient && patientSlug) patientSlug = patientSlug + '-' + randomUuid().slice(0, 6);
    else if (!patientSlug) patientSlug = 'pat-' + randomUuid().slice(0, 8);
  }

  // Walk-in queue token via the injected $mint-token seam (minted BEFORE the
  // bundle so it rides the Encounter atomically). Best-effort: a mint failure
  // must not fail the booking — the desk can assign a token at check-in.
  let token = null;
  if (walkIn && typeof mintToken === 'function') {
    try { token = (await mintToken(departmentId)) || null; } catch (_) { token = null; }
  }

  const department = await resolveDepartmentOrg(medplum, departmentId, departmentName);
  const deptDisplay = departmentName || (department && department.display) || undefined;
  let appointmentSlug = null;
  let encounterSlug = null;
  if (idempotencyKey) {
    const key = String(idempotencyKey).trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9-]{0,79}$/.test(key)) throw new Error('invalid booking idempotency key');
    appointmentSlug = `appt-${key}`;
    encounterSlug = `enc-${key}`;
  }
  // Stable database-level guard for an advance slot. Medplum 5.1.22 executes
  // conditional creates in a serializable transaction, so concurrent desks
  // converge on one active Appointment+Encounter for this doctor/start instant.
  // Cancelled rows are excluded, allowing the slot to be booked again.
  const slotKey = (!walkIn && doctorId)
    ? `slot-${String(doctorId).replace(/[^a-zA-Z0-9-]/g, '-')}-${Date.parse(start)}`
    : null;

  const { bundle, keys } = buildBookingBundle({
    patient, patientSlug, patientId, start, end: endISO,
    doctorId, doctorName, department, departmentName: deptDisplay, token, reason,
    appointmentSlug, encounterSlug, slotKey,
  });

  // ATOMIC COMMIT — all three land or none (fail loud on a rejected transaction).
  const response = await medplum.executeBatch(bundle);
  try { medplum.invalidateSearches('Encounter'); medplum.invalidateSearches('Appointment'); medplum.invalidateSearches('Patient'); } catch (_) {}

  // Map the transaction-response back to real ids. FHIR preserves entry order 1:1
  // with the request, so we walk them in the order we pushed (Patient?, Appt, Enc).
  const respEntries = (response && response.entry) || [];
  let i = 0;
  let patientResolvedId = patientId || null;
  let patientExisted = false;
  let patientResource = null;
  if (keys.hasPatientEntry) {
    const e = respEntries[i++] || {};
    patientResolvedId = idFromLocation(e.response && e.response.location) || (e.resource && e.resource.id) || null;
    patientExisted = String((e.response && e.response.status) || '').startsWith('200'); // ifNoneExist matched an existing Patient
    patientResource = e.resource || null;
  }
  const apptEntry = respEntries[i++] || {};
  const encEntry = respEntries[i++] || {};
  const appointmentId = idFromLocation(apptEntry.response && apptEntry.response.location) || (apptEntry.resource && apptEntry.resource.id) || null;
  const encounterId = idFromLocation(encEntry.response && encEntry.response.location) || (encEntry.resource && encEntry.resource.id) || null;

  if (!encounterId) {
    // The transaction returned no Encounter id — treat as a failed booking rather
    // than hand the caller a broken /consult link (fail-loud, redesign invariant).
    throw new Error('booking transaction returned no Encounter id');
  }

  // Best-effort intake magic link (never blocks / fails the booking).
  let link = null;
  let magicToken = null;
  if (sendLink && doFetch) {
    const sent = await sendMagicLink(encounterId, { fetchImpl: doFetch, authHeader: doAuth });
    link = sent.link;
    magicToken = sent.token;
  }

  // Shape the created resources for the DTO. Prefer the server-returned resource;
  // else reconstruct from the body we sent + the resolved id. UUIDs are the PRIMARY
  // ids AND _medplumId is populated (Tasks.jsx book() reads encounter._medplumId).
  const encBodySent = bundle.entry[bundle.entry.length - 1].resource;
  const apptBodySent = bundle.entry[bundle.entry.length - 2].resource;
  // Conditional retry responses commonly omit the matched resource body. Read
  // it back when status=200 so the DTO reflects the EXISTING visit/token rather
  // than reconstructing the newly attempted body (which may carry a newly minted
  // token that was not committed because the Encounter already existed).
  const apptExisting = String(apptEntry.response?.status || '').startsWith('200');
  const encExisting = String(encEntry.response?.status || '').startsWith('200');
  const apptActual = apptEntry.resource || (apptExisting && appointmentId ? await medplum.readResource('Appointment', appointmentId) : null);
  const encActual = encEntry.resource || (encExisting && encounterId ? await medplum.readResource('Encounter', encounterId) : null);
  if (slotKey && apptExisting) {
    const bookedPatientRef = (apptActual?.participant || [])
      .find((p) => p?.actor?.reference?.startsWith('Patient/'))?.actor?.reference || '';
    const expectedPatientRef = patientResolvedId ? `Patient/${patientResolvedId}` : '';
    if (!expectedPatientRef || bookedPatientRef !== expectedPatientRef) {
      throw Object.assign(new Error('slot-taken'), { code: 'slot-taken', status: 409, slot: start, doctorId });
    }
  }
  const encounter = {
    ...(encActual || encBodySent),
    id: encounterId,
    _medplumId: encounterId,
    _opdSlug: keys.encSlug,
  };
  const appointment = appointmentId
    ? { ...(apptActual || apptBodySent), id: appointmentId, _medplumId: appointmentId }
    : null;
  const patientOut = patientResolvedId
    ? {
        ...(patientResource || (keys.hasPatientEntry ? bundle.entry[0].resource : {})),
        id: patientResolvedId,
        _medplumId: patientResolvedId,
      }
    : null;

  return {
    ok: true,
    encounterId,                 // Medplum UUID — the online-primary PRIMARY id
    encounterSlug: keys.encSlug, // the OPD slug (disk-native key / row-flash key)
    appointmentId,
    patientId: patientResolvedId,
    patientExisted,              // true → the conditional-create matched an existing Patient (idempotent rebook)
    token: (encounter.extension || []).find((x) => x?.url === OPD_TOKEN_EXT)?.valueString || null,
    walkIn,
    link,                        // best-effort intake magic link (null if the send was denied/failed)
    magicToken,                  // best-effort magic-link token
    source: source || null,
    patient: patientOut,
    appointment,
    encounter,
  };
}

/**
 * cancelAppointment({ appointmentId, encounterId? }) — ONLINE-PRIMARY cancel,
 * run AS THE LOGGED-IN CALLER (RLS the only lock). Sets Appointment.status →
 * 'cancelled' and cancels the linked planned Encounter(s) so the slot frees on
 * the availability board (medplum-availability excludes cancelled) and the row
 * leaves the schedule (medplum-schedule excludes cancelled at the query).
 * Only a not-yet-started visit is cancellable: an Encounter that has progressed
 * beyond 'planned'/'arrived' (consult underway or done) refuses loudly rather
 * than silently cancelling clinical work.
 */
export async function cancelAppointment({ appointmentId, encounterId = null }) {
  if (!appointmentId) throw new Error('cancelAppointment requires an appointmentId');
  const { medplum } = await getMedplum();

  const appt = await medplum.readResource('Appointment', appointmentId);
  if (appt.status === 'cancelled') return { ok: true, alreadyCancelled: true, encountersCancelled: 0 };

  // Guard: refuse when the visit has already started (the schedule row's state
  // comes from the Encounter, so check the real record, not the board DTO).
  const encs = await medplum.searchResources('Encounter', `appointment=${encodeURIComponent(appointmentId)}&_count=10`);
  const linked = Array.isArray(encs) ? encs : [];
  if (encounterId && !linked.some((e) => e.id === encounterId)) {
    try { linked.push(await medplum.readResource('Encounter', encounterId)); } catch (_) { /* board enc may be stale — appointment link is authoritative */ }
  }
  const started = linked.find((e) => !['planned', 'arrived', 'cancelled'].includes(e.status));
  if (started) throw new Error(`This visit has already started (encounter is ${started.status}) — finish or discharge it from the consult instead.`);

  await medplum.updateResource({ ...appt, status: 'cancelled' });
  let encountersCancelled = 0;
  for (const enc of linked) {
    if (enc.status === 'cancelled') continue;
    await medplum.updateResource({ ...enc, status: 'cancelled' });
    encountersCancelled += 1;
  }
  return { ok: true, alreadyCancelled: false, encountersCancelled };
}
